import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  ShieldHalf,
  ShieldCheck,
  Lock,
  Fingerprint,
  FileCheck2,
  Cloud,
  Radar,
  Eye,
  EyeOff,
  Loader2,
  Globe,
  HeartPulse,
  Home,
  TowerControl,
  Compass,
  BadgeCheck,
  Aperture,
  Heart,
  BookOpen,
  Brain,
  Megaphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const LIGHTHOUSE =
  "https://images.unsplash.com/photo-1651341772765-0e5bb9a909b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const FEATURES = [
  { Icon: Fingerprint, title: "Unified Identity", sub: "One identity across all products", color: "text-teal-600", bg: "bg-teal-50" },
  { Icon: Lock, title: "End-to-End Encryption", sub: "Your data is always protected", color: "text-blue-600", bg: "bg-blue-50" },
  { Icon: ShieldCheck, title: "Privacy by Design", sub: "Built with control, consent, and choice", color: "text-emerald-600", bg: "bg-emerald-50" },
  { Icon: FileCheck2, title: "Audit & Governance", sub: "Complete visibility and audit trails", color: "text-indigo-600", bg: "bg-indigo-50" },
  { Icon: Cloud, title: "Secure Delivery", sub: "Trusted, monitored, and resilient", color: "text-orange-500", bg: "bg-orange-50" },
];

const PRODUCTS = [
  { Icon: HeartPulse, name: "MDD", tag: "Migraine Data Detective", color: "text-rose-500", bg: "bg-rose-50" },
  { Icon: Home, name: "The Well", tag: "Family Calendar with a Heart", color: "text-amber-500", bg: "bg-amber-50" },
  { Icon: TowerControl, name: "Lighthouse", tag: "Trauma-Informed Media Guidance", color: "text-sky-500", bg: "bg-sky-50" },
  { Icon: Compass, name: "LifeOS", tag: "Situational Awareness for Life", color: "text-emerald-500", bg: "bg-emerald-50" },
  { Icon: BadgeCheck, name: "Evident", tag: "Evidence for Veterans", color: "text-violet-500", bg: "bg-violet-50" },
  { Icon: Aperture, name: "Mirror", tag: "Self-Understanding, Stronger Connections", color: "text-slate-500", bg: "bg-slate-100" },
  { Icon: Heart, name: "Kindred", tag: "Relationships Done Right", color: "text-red-500", bg: "bg-red-50" },
  { Icon: BookOpen, name: "Book Nook Exchange", tag: "Stories, Community, Second Chances", color: "text-orange-500", bg: "bg-orange-50" },
  { Icon: Brain, name: "MemIQ", tag: "Your Personal Memory OS", color: "text-blue-500", bg: "bg-blue-50" },
  { Icon: Megaphone, name: "Brand OS", tag: "Marketing Intelligence Layer", color: "text-purple-500", bg: "bg-purple-50" },
];

const TRUST = [
  { Icon: ShieldCheck, title: "Zero-Trust Architecture", sub: "Verify. Never assume." },
  { Icon: Lock, title: "Privacy First Always", sub: "You own your data." },
  { Icon: Radar, title: "24/7 Monitoring & Threat Detection", sub: "Always watching. Always learning." },
  { Icon: FileCheck2, title: "Compliance & Standards", sub: "Built to meet and exceed." },
];

export default function Login() {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/");
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-sky-100 via-blue-50 to-white text-slate-800 flex flex-col">
      <div className="flex-1 w-full max-w-[1500px] mx-auto px-6 lg:px-10 pt-8 pb-6">
        {/* Top bar */}
        <header className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center shadow-sm">
              <ShieldHalf className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold tracking-[0.18em] text-slate-800 text-sm sm:text-base">
              PRECIPICE HEALTH
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg border border-blue-200 bg-white/70 flex items-center justify-center">
              <ShieldHalf className="w-4 h-4 text-blue-600" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-slate-800">Precipice Forge</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-blue-500">Operations Console</div>
            </div>
          </div>
        </header>

        {/* Hero + login */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left */}
          <div className="lg:col-span-7 relative">
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl text-slate-900 leading-[0.95]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="landing-heading"
            >
              Precipice Forge
            </h1>
            <p className="text-lg sm:text-xl text-blue-600 mt-3 font-medium">Security operations console</p>

            <p className="text-base sm:text-lg font-semibold text-slate-800 mt-6">
              One security layer. Every Precipice Health product.
            </p>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed mt-3 max-w-2xl">
              Precipice Forge is the shared security and trust foundation protecting identity, access,
              privacy, encrypted communications, audit trails, and secure delivery across the entire
              product line—including <span className="text-blue-600 font-medium">Brand OS</span>, our
              marketing intelligence layer.
            </p>

            {/* Feature cards */}
            <div className="relative mt-8">
              <img
                src={LIGHTHOUSE}
                alt="Lighthouse"
                className="hidden xl:block absolute -left-4 top-2 w-40 h-52 object-cover rounded-xl shadow-lg ring-1 ring-white/60 opacity-95"
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4 xl:pl-40">
                {FEATURES.map((f) => (
                  <div key={f.title} className="text-center xl:text-left">
                    <div className={`w-14 h-14 rounded-2xl ${f.bg} flex items-center justify-center mx-auto xl:mx-0 mb-3`}>
                      <f.Icon className={`w-6 h-6 ${f.color}`} />
                    </div>
                    <div className="text-sm font-semibold text-slate-800">{f.title}</div>
                    <div className="text-xs text-slate-500 leading-snug mt-1">{f.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — login card */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-3xl shadow-xl ring-1 ring-slate-200/70 p-8 sm:p-10" data-testid="login-card">
              <h2 className="text-3xl font-semibold text-slate-900">Welcome back</h2>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                Sign in to manage the security and privacy foundation for Precipice Health.
              </p>

              <form onSubmit={submit} className="mt-7 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                  <Input
                    type="email"
                    data-testid="login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@precipice-health.com"
                    required
                    className="h-12 rounded-xl bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                  <div className="relative">
                    <Input
                      type={showPw ? "text" : "password"}
                      data-testid="login-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••••"
                      required
                      className="h-12 rounded-xl bg-white border-slate-200 text-slate-800 pr-11 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                    />
                    <button
                      type="button"
                      data-testid="toggle-password"
                      onClick={() => setShowPw((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                    >
                      {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <Checkbox
                      checked={remember}
                      onCheckedChange={(v) => setRemember(!!v)}
                      data-testid="remember-me"
                      className="border-slate-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    Remember me
                  </label>
                  <button type="button" className="text-sm text-blue-600 hover:text-blue-700 transition-colors duration-200">
                    Forgot password?
                  </button>
                </div>

                {error && (
                  <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit"
                  className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-base font-medium shadow-md shadow-blue-600/20 transition-colors duration-200"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin mr-1" /> : <Lock className="w-4 h-4 mr-2" />}
                  {loading ? "Signing in…" : "Sign in securely"}
                </Button>
              </form>

              <div className="mt-6 flex items-start gap-3 rounded-2xl bg-blue-50/70 ring-1 ring-blue-100 p-4">
                <div className="w-9 h-9 rounded-full bg-white ring-1 ring-blue-200 flex items-center justify-center shrink-0">
                  <Lock className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-800">Protected by Precipice Forge</div>
                  <div className="text-xs text-slate-500 leading-relaxed mt-0.5">
                    Zero-trust architecture. Continuous monitoring. Built for privacy and resilience.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ecosystem */}
        <div className="mt-12">
          <div className="flex items-center gap-4 mb-6">
            <span className="text-sm font-medium text-slate-500 whitespace-nowrap">Securing the Precipice Health Ecosystem</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-4" data-testid="ecosystem-grid">
            {PRODUCTS.map((p) => (
              <div key={p.name} className="text-center" data-testid={`product-${p.name.toLowerCase().replace(/\s/g, "-")}`}>
                <div className={`w-12 h-12 rounded-full ${p.bg} flex items-center justify-center mx-auto mb-2`}>
                  <p.Icon className={`w-5 h-5 ${p.color}`} />
                </div>
                <div className="text-xs font-semibold text-slate-800 leading-tight">{p.name}</div>
                <div className="text-[10px] text-slate-500 leading-snug mt-0.5">{p.tag}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust bar */}
        <div className="mt-10 rounded-2xl bg-white/60 ring-1 ring-slate-200/70 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {TRUST.map((t) => (
                <div key={t.title} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                    <t.Icon className="w-4 h-4 text-slate-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{t.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{t.sub}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 xl:justify-end">
              <div>
                <div className="text-sm font-semibold text-slate-800">Explore the Precipice Health family</div>
                <p className="text-xs text-slate-500 mt-1 max-w-xs">
                  Add your name to the waitlist for the product or products that matter to you.
                </p>
                <a
                  href="https://precipice-health.com"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="explore-link"
                  className="inline-flex items-center gap-2 mt-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 transition-colors duration-200"
                >
                  <Globe className="w-4 h-4" /> precipice-health.com
                </a>
              </div>
              <div className="bg-white p-2 rounded-lg ring-1 ring-slate-200 shrink-0">
                <QRCodeSVG value="https://precipice-health.com" size={84} fgColor="#0f172a" bgColor="#ffffff" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer quote */}
      <footer className="bg-[#0a1b3d] text-center py-4 px-6">
        <p className="text-sm text-blue-100/90">
          Data is the <span className="font-medium" style={{ color: "#D8C9A3" }}>currency</span> of transparency, the{" "}
          <span className="font-medium" style={{ color: "#D8C9A3" }}>gateway</span> to evidence, and the{" "}
          <span className="font-medium" style={{ color: "#D8C9A3" }}>precipice</span> of power. — Precipice Health
        </p>
      </footer>
    </div>
  );
}
